import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ThumbsUp, BellRing, BellOff, MessageSquare, Camera, Loader2, Send, EyeOff, Eye, X, CalendarDays, Clock, AlertTriangle } from "lucide-react";
import { api, errMsg, fdate, compressImage } from "@/lib/api";
import { useFetch } from "@/lib/hooks";

/** Votar y suscribirse a un reporte público. */
export function SocialBar({ folio, state, onChange, compact = false }) {
  const [busy, setBusy] = useState(null);
  const act = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/reports/${folio}/${kind}`);
      onChange?.(data);
      if (kind === "subscribe") toast.success(data.subscribed ? "Te avisaremos de cada avance" : "Dejaste de seguir este reporte");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const size = compact ? "sm" : "default";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`social-bar-${folio}`}>
      <Button size={size} variant={state.voted ? "default" : "outline"} onClick={(e) => { e.stopPropagation(); act("vote"); }} disabled={!!busy} className="btn-press" data-testid={`vote-button-${folio}`}>
        {busy === "vote" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsUp className="h-4 w-4" />}
        <span className="ml-1.5 tabular-nums" data-testid={`vote-count-${folio}`}>{state.vote_count ?? 0}</span>
        {!compact && <span className="ml-1">{state.voted ? "Votado" : "Me afecta"}</span>}
      </Button>
      <Button size={size} variant={state.subscribed ? "secondary" : "outline"} onClick={(e) => { e.stopPropagation(); act("subscribe"); }} disabled={!!busy} className="btn-press" data-testid={`subscribe-button-${folio}`}>
        {busy === "subscribe" ? <Loader2 className="h-4 w-4 animate-spin" /> : state.subscribed ? <BellOff className="h-4 w-4" /> : <BellRing className="h-4 w-4" />}
        {!compact && <span className="ml-1.5">{state.subscribed ? "Siguiendo" : "Seguir avances"}</span>}
      </Button>
    </div>
  );
}

/** Comentarios e imágenes de vecinos; los funcionarios pueden ocultarlos. */
export function CommentsPanel({ folio, staff = false, onCount }) {
  const list = useFetch(`/reports/${folio}/comments`, { poll: 10000, deps: [folio] });
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/reports/${folio}/comments`, { text, photo });
      setText(""); setPhoto(null); list.reload(); onCount?.();
      toast.success("Comentario publicado");
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  const moderate = async (c) => {
    try { await api.patch(`/intranet/comments/${c.comment_id}`, { hidden: !c.hidden }); list.reload(); toast.success(c.hidden ? "Comentario restablecido" : "Comentario ocultado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const items = list.data || [];
  return (
    <div className="space-y-3" data-testid="comments-panel">
      <div className="space-y-2">
        {!items.length && <div className="text-sm text-muted-foreground">Aún no hay comentarios. Sé el primero en sumar información.</div>}
        {items.map((c) => (
          <div key={c.comment_id} className={`rounded-lg border p-3 ${c.hidden ? "bg-slate-50 opacity-70" : "bg-white"}`} data-testid={`comment-${c.comment_id}`}>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-sm">{c.author}</span>
              {c.author_role !== "ciudadano" && <Badge variant="outline" className="text-[10px] bg-[#DFF3F6] border-[#b9e2e8] text-[#006A7C]">Gobierno</Badge>}
              {c.is_demo && <Badge variant="outline" className="text-[10px]">DEMO</Badge>}
              {c.hidden && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-800 border-rose-200">Oculto</Badge>}
              <span className="text-muted-foreground font-mono ml-auto">{fdate(c.created_at)}</span>
              {staff && (
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => moderate(c)} data-testid={`comment-moderate-${c.comment_id}`}>
                  {c.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}<span className="ml-1 text-xs">{c.hidden ? "Mostrar" : "Ocultar"}</span>
                </Button>
              )}
            </div>
            <p className="text-sm mt-1">{c.text}</p>
            {c.photo && <img src={c.photo} alt="aporte vecinal" className="mt-2 max-h-48 rounded-md border object-cover" />}
          </div>
        ))}
      </div>
      <div className="rounded-lg border bg-secondary/40 p-3 space-y-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Agrega información o una foto que ayude a la dependencia…" data-testid="comment-input" />
        <div className="flex items-center gap-2">
          {photo ? (
            <span className="relative"><img src={photo} alt="adjunto" className="h-12 w-12 rounded object-cover border" />
              <button className="absolute -top-1.5 -right-1.5 rounded-full bg-white shadow p-0.5" onClick={() => setPhoto(null)} aria-label="Quitar"><X className="h-3 w-3" /></button></span>
          ) : (
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer rounded-md border bg-white px-2.5 py-1.5 hover:bg-secondary transition-colors">
              <Camera className="h-3.5 w-3.5 text-primary" />Imagen
              <input type="file" accept="image/*" className="hidden" data-testid="comment-photo-input"
                onChange={async (e) => { const f = e.target.files?.[0]; if (f) setPhoto(await compressImage(f, 1024, 0.72)); }} />
            </label>
          )}
          <Button size="sm" className="ml-auto" onClick={send} disabled={sending || text.trim().length < 2} data-testid="comment-submit-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="ml-1">Publicar</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

export const CommentCount = ({ n }) => <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><MessageSquare className="h-3.5 w-3.5" />{n || 0}</span>;

/** Render de campos configurados en el constructor de formularios. */
export function DynamicFields({ fields = [], values, onChange, prefix = "custom" }) {
  if (!fields.length) return null;
  const set = (k, v) => onChange({ ...values, [k]: v });
  return (
    <div className="space-y-3" data-testid={`${prefix}-fields`}>
      {fields.map((f) => (
        <div key={f.key} className="space-y-1.5">
          {f.type !== "checkbox" && <Label htmlFor={`${prefix}-${f.key}`}>{f.label}{f.required && <span className="text-rose-600"> *</span>}</Label>}
          {f.type === "textarea" ? <Textarea id={`${prefix}-${f.key}`} rows={2} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`${prefix}-field-${f.key}`} />
            : f.type === "select" ? (
              <Select value={values[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                <SelectTrigger id={`${prefix}-${f.key}`} data-testid={`${prefix}-field-${f.key}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : f.type === "checkbox" ? (
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!values[f.key]} onCheckedChange={(v) => set(f.key, !!v)} data-testid={`${prefix}-field-${f.key}`} />{f.label}{f.required && <span className="text-rose-600">*</span>}</label>
            ) : <Input id={`${prefix}-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`${prefix}-field-${f.key}`} />}
          {f.help && <div className="text-xs text-muted-foreground">{f.help}</div>}
        </div>
      ))}
    </div>
  );
}

export const missingRequired = (fields = [], values = {}) => fields.filter((f) => f.required && (values[f.key] === undefined || values[f.key] === "" || values[f.key] === false)).map((f) => f.label);

/** Plazo de atención con semáforo. */
export function DueBadge({ due, status, testid }) {
  if (!due) return null;
  if (["resuelto", "cerrado", "rechazado"].includes(status)) return <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid={testid}><Clock className="h-3 w-3" />Plazo {fdate(due)}</span>;
  const h = (new Date(due) - Date.now()) / 3600000;
  const tone = h < 0 ? "bg-rose-50 text-rose-800 border-rose-200" : h < 24 ? "bg-amber-50 text-amber-900 border-amber-200" : "bg-emerald-50 text-emerald-800 border-emerald-200";
  const label = h < 0 ? `Vencido hace ${fmtH(-h)}` : `Vence en ${fmtH(h)}`;
  return <Badge variant="outline" className={`text-[10px] font-medium gap-1 ${tone}`} data-testid={testid}>{h < 0 ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{label}</Badge>;
}
const fmtH = (h) => (h >= 48 ? `${Math.round(h / 24)} d` : h >= 1 ? `${Math.round(h)} h` : `${Math.max(1, Math.round(h * 60))} min`);

/** Portada de evento con respaldo visual si no hay imagen. */
export function EventCover({ event, className = "h-40" }) {
  const d = new Date(event.starts_at);
  if (event.cover) return <img src={event.cover} alt={event.title} className={`w-full object-cover ${className}`} />;
  return (
    <div className={`relative w-full overflow-hidden bg-[#0F4C5C] text-white ${className}`}>
      <div className="absolute inset-0 tech-grid opacity-60" />
      <div className="relative h-full flex items-end justify-between p-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#a9d6de]">{d.toLocaleDateString("es-MX", { weekday: "long" })}</div>
          <div className="font-display text-4xl font-bold leading-none">{d.getDate()}</div>
          <div className="text-sm text-white/80">{d.toLocaleDateString("es-MX", { month: "long", year: "numeric" })}</div>
        </div>
        <CalendarDays className="h-10 w-10 text-[#7fd6e2] opacity-70" />
      </div>
    </div>
  );
}

/** Convierte ISO a valor de <input type="datetime-local"> en hora local. */
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
